import { syntaxTree } from '@codemirror/language';
import { Diagnostic, linter } from '@codemirror/lint';
import { EditorView } from '@codemirror/view';
import { SyntaxNode } from '@lezer/common';
import { FunctionCall, FunctionIdentifier, Identifier, VectorSelector } from '@/prometheus/lezer/parser.terms';
import { PrometheusMetadataClient } from '../client/metadata';
import { walkBackward } from '../parser';
import { createPromQLEditorView } from './helpers';

/**
 * Checks if the given function name is a range function.
 */
function isRangeFn(name: string | null) {
  return name === 'sum_per_second' || name === 'increase' || name === 'rate' || name === 'irate';
}

function isOverTimeFn(name: string | null) {
  if (!name) {
    return false;
  }
  return name.includes('over_time');
}

function isDeltaCounterRangeFn(name: string | null) {
  if (!name) {
    return false;
  }
  return name === 'sum_per_second' || name.includes('over_time');
}

const ERROR_MESSAGES = {
  DELTA_COUNTER: {
    NO_RANGE_FUNCTION: 'Use sum_per_second() or sum_over_time() when querying a delta counter.',
    WRONG_RANGE_FUNCTION:
      'The metric is a delta counter. Use sum_per_second() or sum_over_time() to get more accurate values when querying delta counters.',
  },
  CUMULATIVE_COUNTER: {
    NO_RANGE_FUNCTION: 'Use rate() or increase() when querying a cumulative counter.',
    WRONG_RANGE_FUNCTION:
      'The metric is a cumulative counter. Use rate() or increase() to get more accurate values when querying cumulative counters.',
  },
  GAUGE: {
    RANGE_FUNCTION: (fn: string) => `The ${fn}() function is not recommended for gauges.`,
  },
};

export async function getPromQLMetadataDiagnotics(view: EditorView, metadata: PrometheusMetadataClient) {
  const getNodeText = (node: SyntaxNode | null | undefined) => {
    if (!node) {
      return null;
    }
    return view.state.sliceDoc(node.from, node.to);
  };
  const diagnostics: Diagnostic[] = [];
  const metricNodes: SyntaxNode[] = [];
  const tree = syntaxTree(view.state);
  tree.cursor().iterate((node) => {
    if (node.node.type.is(Identifier)) {
      metricNodes.push(node.node);
    }
  });

  // For each metric node, check its type and add a diagnostic if needed
  for (const node of metricNodes) {
    const metricName = getNodeText(node);
    const vectorSelectorNode = walkBackward(node, VectorSelector);

    if (!metricName || vectorSelectorNode === null) {
      continue;
    }

    const functionCallNode = walkBackward(node, FunctionCall);
    let functionIdentifierNode: SyntaxNode | null = null;
    functionCallNode?.cursor().iterate((node) => {
      if (node.type.is(FunctionIdentifier)) {
        functionIdentifierNode = node.node;
        return false;
      }
      return true;
    });

    const rangeFunctionText = getNodeText(functionIdentifierNode);
    const hasRangeFunction = isRangeFn(rangeFunctionText);
    const hasOverTimeFunction = isOverTimeFn(rangeFunctionText);

    const hasFunction = hasRangeFunction || hasOverTimeFunction;

    const metricType = await metadata.getMetricType(metricName);
    if (metricType === 'DELTA_COUNTER') {
      if (!hasFunction) {
        diagnostics.push({
          from: node.from,
          to: node.to,
          actions: [
            {
              name: 'Fix',
              apply: (view) => {
                const newText = `sum_per_second(${getNodeText(vectorSelectorNode)}[$__rate_interval])`;
                view.dispatch({
                  changes: {
                    from: vectorSelectorNode.from,
                    to: vectorSelectorNode.to,
                    insert: newText,
                  },
                });
              },
            },
          ],
          severity: 'warning',
          message: ERROR_MESSAGES.DELTA_COUNTER.NO_RANGE_FUNCTION,
        });
      } else if (functionCallNode && !isDeltaCounterRangeFn(rangeFunctionText)) {
        diagnostics.push({
          from: node.from,
          to: node.to,
          actions: [
            {
              name: 'Fix',
              apply: (view) => {
                const newText = `sum_per_second(${getNodeText(vectorSelectorNode)}[$__rate_interval])`;
                view.dispatch({
                  changes: {
                    from: functionCallNode.from,
                    to: functionCallNode.to,
                    insert: newText,
                  },
                });
              },
            },
          ],
          severity: 'warning',
          message: ERROR_MESSAGES.DELTA_COUNTER.WRONG_RANGE_FUNCTION,
        });
      }
    } else if (metricType === 'CUMULATIVE_COUNTER') {
      if (!hasFunction) {
        diagnostics.push({
          from: node.from,
          to: node.to,
          actions: [
            {
              name: 'Fix',
              apply: (view) => {
                const newText = `rate(${getNodeText(vectorSelectorNode)}[$__rate_interval])`;
                view.dispatch({
                  changes: {
                    from: vectorSelectorNode.from,
                    to: vectorSelectorNode.to,
                    insert: newText,
                  },
                });
              },
            },
          ],
          severity: 'warning',
          message: ERROR_MESSAGES.CUMULATIVE_COUNTER.NO_RANGE_FUNCTION,
        });
      } else if (functionCallNode && (!isRangeFn(rangeFunctionText) || rangeFunctionText?.includes('sum_per_second'))) {
        diagnostics.push({
          from: node.from,
          to: node.to,
          actions: [
            {
              name: 'Fix',
              apply: (view) => {
                const newText = `rate(${getNodeText(vectorSelectorNode)}[$__rate_interval])`;
                view.dispatch({
                  changes: {
                    from: functionCallNode.from,
                    to: functionCallNode.to,
                    insert: newText,
                  },
                });
              },
            },
          ],
          severity: 'warning',
          message: ERROR_MESSAGES.CUMULATIVE_COUNTER.WRONG_RANGE_FUNCTION,
        });
      }
    } else if (metricType === 'GAUGE') {
      if (hasRangeFunction && functionCallNode) {
        diagnostics.push({
          from: node.from,
          to: node.to,
          actions: [
            {
              name: 'Fix',
              apply: (view) => {
                const newText = `${getNodeText(vectorSelectorNode)}`;
                view.dispatch({
                  changes: {
                    from: functionCallNode.from,
                    to: functionCallNode.to,
                    insert: newText,
                  },
                });
              },
            },
          ],
          severity: 'warning',
          message: ERROR_MESSAGES.GAUGE.RANGE_FUNCTION(rangeFunctionText || ''),
        });
      }
    }
  }

  return diagnostics.map((d) => ({
    ...d,
    renderMessage: () => {
      const el = document.createElement('div');
      el.style.maxWidth = '500px';
      el.style.marginBottom = '8px';
      el.textContent = d.message;
      return el;
    },
  }));
}

export function promQLMetricMetadataLinterExtension({ client }: { client: PrometheusMetadataClient }) {
  const metadata = client;
  return linter(
    async (view) => {
      return getPromQLMetadataDiagnotics(view, metadata);
    },
    {
      delay: 1000,
    }
  );
}

export async function getDiagnosticsFromPromQLQuery(query: string, metadata: PrometheusMetadataClient) {
  const view = createPromQLEditorView(query);
  const d = await getPromQLMetadataDiagnotics(view, metadata);

  const diagnostics = d.map((diag) => ({
    ...diag,
    // Augment the fix function to apply the action and return the new query
    fix: () => {
      diag.actions?.forEach((action) => {
        action.apply(view, 0, view.state.doc.length);
      });
      return view.state.doc.toString();
    },
  }));

  return diagnostics;
}
